import { useMemo } from 'react';
import { Checkbox } from '@sweep-io/sweep-design';
import { DataTableBaseColumn, DataTableRow } from './TableTypes';
import { SELECTION_COLUMN_FIELD } from './utils';

interface UseSelectableColumnsOptions {
  selectedRowIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  selectAllProps?: {
    disabled?: boolean;
    indeterminate?: boolean;
  };
}

export function useSelectableColumns<TRow extends DataTableRow = DataTableRow>(
  originalColumns: DataTableBaseColumn[],
  rows: TRow[],
  { selectedRowIds, onSelectionChange, selectAllProps }: UseSelectableColumnsOptions,
): DataTableBaseColumn[] {
  // Create the selection checkbox column
  const selectionColumn: DataTableBaseColumn = useMemo(() => {
    return {
      field: SELECTION_COLUMN_FIELD as any, // Type assertion since this field doesn't exist on rows
      // this is for the header functionality only - to select or deselect all rows, plus indicate for intermediate state
      headerName: (
        <Checkbox
          checked={selectedRowIds.length > 0 && selectedRowIds.length === rows.length}
          indeterminate={
            selectAllProps?.indeterminate ??
            (selectedRowIds.length > 0 && selectedRowIds.length < rows.length)
          }
          disabled={selectAllProps?.disabled ?? rows.length === 0}
          onChange={(event) => {
            if (event.target.checked) {
              onSelectionChange(rows.map((row) => row.id));
            } else {
              onSelectionChange([]);
            }
          }}
        />
      ),
      width: '60px',
      minWidth: '60px',
      maxWidth: '60px',
      sortable: false,
      renderer: (_value, _id, _field, row) => (
        <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedRowIds.includes(row.id)}
            onChange={(event) => {
              if (event.target.checked) {
                onSelectionChange([...selectedRowIds, row.id]);
              } else {
                onSelectionChange(selectedRowIds.filter((selectedId) => selectedId !== row.id));
              }
            }}
          />
        </div>
      ),
    };
  }, [selectedRowIds, onSelectionChange, selectAllProps, rows]);

  // Combine selection column with original columns
  return useMemo(() => {
    return [selectionColumn, ...originalColumns];
  }, [selectionColumn, originalColumns]);
}

export default useSelectableColumns;
